import {
    addDemoDays,
    formatDemoDisplayDate,
    parseDemoDateTime,
    shiftDemoDate,
} from '../../../../data/mockDemoDates';

const outcomeByStatus = {
    Validated: 'verified',
    Valid: 'verified',
    Verified: 'verified',
    'Inconsistent data': 'needs_review',
    'Invalid document': 'rejected',
    'Not matching': 'rejected',
};

const fileMetaById = {
    1: { format: 'PDF', size: '1.2 MB' },
    2: { format: 'PDF', size: '750 KB' },
    3: { format: 'PDF', size: '640 KB' },
    4: { format: 'PDF', size: '320 KB' },
};

const forensicsByOutcome = {
    verified: [
        { title: 'Document type identified', status: 'passed' },
        { title: 'OCR completed (98% confidence)', status: 'passed' },
        { title: 'Key fields extracted', status: 'passed' },
        { title: 'Issuing authority verified', status: 'passed' },
        { title: 'Document number validated', status: 'passed' },
        { title: 'Expiry date checked', status: 'passed' },
        { title: 'No tampering indicators', status: 'passed' },
        { title: 'Document is authentic', status: 'passed' },
    ],
    needs_review: [
        { title: 'Document type identified', status: 'passed' },
        { title: 'OCR completed (96% confidence)', status: 'passed' },
        { title: 'Key fields extracted', status: 'passed' },
        { title: 'Issuing authority verification unclear', status: 'warning' },
        { title: 'Document number validated', status: 'passed' },
        { title: 'Expiry date checked', status: 'passed' },
        { title: 'Possible editing artifacts detected', status: 'warning' },
        { title: 'Document is likely authentic', status: 'warning' },
    ],
    rejected: [
        { title: 'Document type identified', status: 'passed' },
        { title: 'OCR completed (92% confidence)', status: 'passed' },
        { title: 'Key fields extracted', status: 'passed' },
        { title: 'Issuing authority could not be verified', status: 'failed' },
        { title: 'Document number validation failed', status: 'failed' },
        { title: 'Suspicious editing artifacts detected', status: 'failed' },
        { title: 'Inconsistent font and layout', status: 'failed' },
        { title: 'Possible tampering indicators', status: 'failed' },
    ],
};

const valueOrDash = (value) => (value && value !== '—' ? value : null);

const getOutcome = (document) => outcomeByStatus[document.status] || 'verified';

const getDocumentNumber = (title, identity) => {
    const registerNumber = valueOrDash(identity.register?.number);
    const vatNumber = valueOrDash(identity.vatNumber);

    if (title === 'Commercial Register') return registerNumber || vatNumber || '—';
    if (title === 'Insurance Certificate' || title === 'ID Document') {
        return registerNumber || vatNumber || '—';
    }

    return vatNumber || registerNumber || '—';
};

const getNumberField = (title) => {
    if (title === 'Commercial Register') return 'Register number';
    if (title === 'Insurance Certificate') return 'Policy number';
    if (title === 'ID Document') return 'Document number';

    return 'License number';
};

const getExtractedStatus = (outcome, field) => {
    if (outcome === 'verified') return 'matched';

    if (outcome === 'needs_review') {
        return field === 'Issuing authority' ? 'unclear' : 'matched';
    }

    if (['License number', 'Register number', 'Policy number', 'Document number'].includes(field)) {
        return 'mismatched';
    }

    if (field === 'Issuing authority') return 'not_verified';

    if (['License type', 'Coverage type', 'Document type', 'Legal form'].includes(field)) {
        return 'low_confidence';
    }

    return 'matched';
};

const getDemoDocumentDates = (submittedAt) => {
    const submittedDate = parseDemoDateTime(submittedAt);
    const issueDate = addDemoDays(submittedDate, -21);
    const expiryDate = addDemoDays(issueDate, 365 * 3);

    return {
        issueDate: formatDemoDisplayDate(issueDate),
        expiryDate: formatDemoDisplayDate(expiryDate),
        submittedOn: formatDemoDisplayDate(submittedDate),
    };
};

const createExtracted = ({
    countryName,
    documentNumber,
    documentDates,
    identity,
    issuingAuthority,
    legalName,
    outcome,
    title,
}) => {
    const licenseType = identity.industry || 'Road freight carrier';
    const director = identity.management?.[0]?.name || legalName;
    const fieldsByTitle = {
        'Commercial Register': [
            { field: 'Company name', value: legalName },
            { field: 'Register number', value: documentNumber },
            { field: 'Issuing authority', value: issuingAuthority },
            { field: 'Issue date', value: documentDates.issueDate },
            { field: 'Legal form', value: identity.legalForm || 'Company' },
            { field: 'Country', value: countryName },
        ],
        'Carrier License': [
            { field: 'Company name', value: legalName },
            { field: 'License number', value: documentNumber },
            { field: 'Issuing authority', value: issuingAuthority },
            { field: 'Issue date', value: documentDates.issueDate },
            { field: 'Expiry date', value: documentDates.expiryDate },
            { field: 'License type', value: licenseType },
            { field: 'Country', value: countryName },
        ],
        'Insurance Certificate': [
            { field: 'Company name', value: legalName },
            { field: 'Policy number', value: documentNumber },
            { field: 'Issuing authority', value: issuingAuthority },
            { field: 'Issue date', value: documentDates.issueDate },
            { field: 'Expiry date', value: documentDates.expiryDate },
            { field: 'Coverage type', value: 'CMR / cargo liability' },
            { field: 'Country', value: countryName },
        ],
        'ID Document': [
            { field: 'Full name', value: director },
            { field: 'Document number', value: documentNumber },
            { field: 'Issuing authority', value: issuingAuthority },
            { field: 'Issue date', value: documentDates.issueDate },
            { field: 'Expiry date', value: documentDates.expiryDate },
            { field: 'Document type', value: 'National ID' },
            { field: 'Country', value: countryName },
        ],
    };

    return (fieldsByTitle[title] || fieldsByTitle['Carrier License']).map((row) => ({
        ...row,
        source: 'Document',
        status: getExtractedStatus(outcome, row.field),
    }));
};

const createCrossChecks = ({
    countryName,
    outcome,
    title,
}) => {
    const numberField = getNumberField(title);
    const source = title === 'Commercial Register'
        ? 'Trade Registry'
        : title === 'Insurance Certificate'
            ? 'Insurer registry'
            : title === 'ID Document'
                ? 'ID authority DB'
                : 'Road Authority DB';

    return [
        {
            field: numberField,
            source,
            result: outcome === 'rejected' ? 'no_match' : outcome === 'needs_review' ? 'unclear' : 'matched',
            details: outcome === 'rejected'
                ? 'Number not found in official database'
                : outcome === 'needs_review'
                    ? 'Record found but details do not fully match'
                    : 'Valid official record',
        },
        {
            field: title === 'ID Document' ? 'Full name' : 'Company name',
            source: 'Trade Registry',
            result: outcome === 'rejected' ? 'partial' : 'matched',
            details: outcome === 'rejected' ? 'Similar name found' : 'Matches company data',
        },
        {
            field: 'Validity status',
            source,
            result: outcome === 'rejected' ? 'no_match' : outcome === 'needs_review' ? 'unclear' : 'matched',
            details: outcome === 'rejected' ? 'Number does not exist' : 'Record is active',
        },
        {
            field: title === 'ID Document' ? 'Nationality' : 'Company address',
            source: 'Company Intelligence',
            result: 'matched',
            details: title === 'ID Document' ? `Matches ${countryName}` : 'Matches registered address',
        },
    ];
};

const createDocumentForensics = (bid) => {
    const identity = bid.companyReports?.[0]?.identity || {};
    const company = bid.company || {};
    const countryName = identity.countryName || company.country || bid.country;
    const legalName = identity.legalName || company.legalName;
    const issuingAuthority = valueOrDash(identity.register?.court) || 'Issuing authority';
    const documentDates = getDemoDocumentDates(
        bid.submittedAt || formatDemoDisplayDate(shiftDemoDate())
    );

    return (bid.documents || []).map((document) => {
        const outcome = getOutcome(document);
        const file = fileMetaById[document.id] || { format: 'PDF', size: '640 KB' };
        const documentNumber = getDocumentNumber(document.title, identity);

        return {
            id: document.id,
            title: document.title,
            preview: document.preview,
            submittedAt: bid.submittedAt,
            submittedOn: documentDates.submittedOn,
            fileFormat: file.format,
            fileSize: file.size,
            outcome,
            information: [
                { label: 'Document type', value: document.title },
                { label: 'Document number', value: documentNumber },
                { label: 'Issuing authority', value: issuingAuthority },
                { label: 'Issue date', value: documentDates.issueDate },
                { label: 'Expiry date', value: documentDates.expiryDate },
                { label: 'Status', value: outcome === 'rejected' ? 'Invalid' : 'Valid', tone: outcome === 'rejected' ? 'danger' : 'success' },
                { label: 'Source', value: 'Carrier submitted' },
                { label: 'Country', value: countryName },
                { label: 'File format', value: `${file.format} (${file.size})` },
            ],
            extracted: createExtracted({
                countryName,
                documentNumber,
                documentDates,
                identity,
                issuingAuthority,
                legalName,
                outcome,
                title: document.title,
            }),
            crossChecks: createCrossChecks({
                countryName,
                outcome,
                title: document.title,
            }),
            forensics: forensicsByOutcome[outcome],
        };
    });
};

export default createDocumentForensics;
